import React, { useState } from 'react';
import Block from './Block';

/**
 * Renders 2–3 option buttons after Phase 4 (predictions), letting the user
 * opt into Phase 5 (explainability), Phase 6 (insights), or both.
 *
 * Props:
 *   blockData.options  – array of { label: string, message: string }
 *   onSend(message)    – called with the option's message when a button is clicked
 *   completed          – { explain: bool, insights: bool }: which of the two
 *                        phases already produced output later in the thread.
 *                        The choice itself is not persisted, so on reload the
 *                        block infers it from what the choice led to.
 */

// The options are fixed strings from the backend (agent_runner.py), read by
// the agent as text, so the same text identifies what each one triggers.
const optionKind = (opt) => {
  const m = String(opt.message || '').toLowerCase();
  if (m.includes('both')) return 'both';
  if (m.includes('drive')) return 'explain';
  if (m.includes('summar')) return 'insights';
  return null;
};

const inferChosen = (options, completed) => {
  if (!completed || (!completed.explain && !completed.insights)) return null;
  const want = completed.explain && completed.insights
    ? 'both'
    : completed.explain ? 'explain' : 'insights';
  const byKind = (k) => options.find((o) => optionKind(o) === k);
  // No "both" option (time series offers insights only): fall back to the
  // single phase that is there.
  const opt = byKind(want) || (want === 'both' && (byKind('insights') || byKind('explain')));
  return opt ? opt.message : null;
};

const AnalysisOptionsBlock = ({ blockData, onSend, completed }) => {
  const [clicked, setClicked] = useState(null);
  const options = (blockData && blockData.options) || [];
  const chosen = clicked ?? inferChosen(options, completed);

  const handleClick = (opt) => {
    if (chosen !== null) return;
    setClicked(opt.message);
    if (onSend) onSend(opt.message);
  };

  return (
    <Block title="What would you like next?" style={{ maxWidth: 480 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {options.map((opt) => {
        const isChosen = chosen === opt.message;
        const isDisabled = chosen !== null && !isChosen;
        return (
          <button
            key={opt.message}
            onClick={() => handleClick(opt)}
            disabled={isDisabled}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 8,
              border: `1px solid ${isChosen ? 'var(--foreground)' : 'var(--border)'}`,
              background: isChosen ? 'var(--muted)' : 'var(--card)',
              color: isDisabled ? 'var(--fg-muted)' : 'var(--fg)',
              cursor: isDisabled ? 'default' : 'pointer',
              fontSize: 14,
              fontWeight: 450,
              textAlign: 'left',
              transition: 'background 0.15s, border-color 0.15s',
              opacity: isDisabled ? 0.45 : 1,
            }}
          >
            <span style={{
              width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
              border: `1.5px solid ${isChosen ? 'var(--accent)' : 'var(--border)'}`,
              background: isChosen ? 'var(--accent)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isChosen && (
                <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                  <path d="M2 5l2.5 2.5 3.5-4" stroke="var(--accent-on)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </span>
            {opt.label}
          </button>
        );
      })}
      </div>
    </Block>
  );
};

export default AnalysisOptionsBlock;
